import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Play, Square, Trash2 } from "lucide-react";

const fmt = (sec) => {
  const h = String(Math.floor(sec / 3600)).padStart(2, "0");
  const m = String(Math.floor((sec % 3600) / 60)).padStart(2, "0");
  const s = String(sec % 60).padStart(2, "0");
  return `${h}:${m}:${s}`;
};

export default function TimeTracking() {
  const [projects, setProjects] = useState([]);
  const [entries, setEntries] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [description, setDescription] = useState("");
  const [startedAt, setStartedAt] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const tickRef = useRef(null);

  const load = async () => {
    const [p, e] = await Promise.all([api.get("/projects"), api.get("/time-entries")]);
    setProjects(p.data);
    setEntries(e.data);
    if (!projectId && p.data[0]) setProjectId(p.data[0].id);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const start = () => {
    if (!projectId) return alert("Pick a project first.");
    setStartedAt(Date.now());
    setElapsed(0);
    tickRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - (startedAt || Date.now())) / 1000));
    }, 250);
  };

  useEffect(() => {
    if (!startedAt) return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 250);
    return () => clearInterval(id);
  }, [startedAt]);

  const stop = async () => {
    if (!startedAt) return;
    clearInterval(tickRef.current);
    const duration = Math.max(1, Math.floor((Date.now() - startedAt) / 1000));
    await api.post("/time-entries", {
      project_id: projectId,
      description,
      duration_seconds: duration,
      started_at: new Date(startedAt).toISOString(),
      ended_at: new Date().toISOString(),
    });
    setStartedAt(null);
    setElapsed(0);
    setDescription("");
    load();
  };

  const remove = async (id) => {
    await api.delete(`/time-entries/${id}`);
    load();
  };

  return (
    <div className="p-8 lg:p-12">
      <header className="mb-8">
        <div className="label-mono text-green-400 mb-2">// session.timer</div>
        <h1 className="font-serif text-4xl lg:text-5xl tracking-tighter" data-testid="time-title">Time</h1>
        <p className="text-neutral-400 text-sm mt-2">Press start. Press stop. The house keeps score.</p>
      </header>

      <div className="panel panel-accent-gold p-8 mb-8 deal">
        <div className="flex items-center gap-8 flex-wrap">
          <div className="font-serif text-6xl tabular-nums glow-green" data-testid="time-display">{fmt(elapsed)}</div>
          <div className="flex-1 min-w-[240px] grid grid-cols-1 md:grid-cols-2 gap-3">
            <label>
              <span className="label-mono block mb-2">Project</span>
              <select data-testid="time-project-select" value={projectId} onChange={(e) => setProjectId(e.target.value)} className="input-shell w-full px-3 py-2 text-sm font-jet">
                {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label>
              <span className="label-mono block mb-2">Description</span>
              <input data-testid="time-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} className="input-shell w-full px-3 py-2 text-sm font-jet" placeholder="What are you doing?" />
            </label>
          </div>
          <div className="flex items-center gap-3">
            {!startedAt ? (
              <button data-testid="time-start-button" onClick={start} className="btn-primary btn-glitch flex items-center gap-2">
                <Play size={14} /> Start
              </button>
            ) : (
              <button data-testid="time-stop-button" onClick={stop} className="btn-ghost flex items-center gap-2 border-red-500 text-red-400 hover:text-red-300">
                <Square size={14} /> Stop & Log
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="panel p-0" data-testid="time-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left label-mono border-b border-neutral-900">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Duration</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500 font-jet text-xs">No sessions logged yet.</td></tr>
            )}
            {entries.map((e, i) => {
              const proj = projects.find((p) => p.id === e.project_id);
              return (
                <tr key={e.id} className={i % 2 === 0 ? "bg-black" : "bg-neutral-950"} data-testid={`time-entry-${e.id}`}>
                  <td className="px-4 py-3 font-jet text-xs text-neutral-400">{(e.started_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-4 py-3">{proj?.name || "—"}</td>
                  <td className="px-4 py-3 text-neutral-300">{e.description || "—"}</td>
                  <td className="px-4 py-3 font-jet text-amber-400">{fmt(e.duration_seconds || 0)}</td>
                  <td className="px-4 py-3 text-right">
                    <button data-testid={`time-delete-${e.id}`} onClick={() => remove(e.id)} className="text-neutral-500 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
